import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SwingieFaceGallery.css";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useFaceGalleryEngine } from "../../shared/useFaceGalleryEngine";
import {
  MSG_LOST,
  MSG_RELEASE_UNCONFIRMED,
  messageForAcquire,
  useFaceClaim,
} from "../../shared/useFaceClaim";
import { returnHomeAfterAdminRelease } from "../../shared/adminRelease";
import HeldFaceOverlay from "../../shared/HeldFaceOverlay";
import HeldGalleryStatus, { useHeldSelectionGuard } from "../../shared/HeldGalleryStatus";
import { FaceGalleryEmpty, FaceGallerySyncChip } from "../../shared/FaceGallerySyncStatus";
import { showAlert } from "../../../../generalUtils";
import { useGif } from "../../../../GifContext";
import gifFaceFailed from "../../../../assets/gifs/result-face_failed.gif";
import gifFailed from "../../../../assets/gifs/result-failed.gif";
import { SERVER_URL } from "../../../../config/serverUrl";
import { getEventScope, isCurrentScope } from "../../../../eventIdentity";

const SwingieFaceGalleryPage = () => {
  const { showGif } = useGif();
  const serverUrl = SERVER_URL;
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const number = parseInt(sessionStorage.getItem("number")) || 1; // Number of faces to select

  // The held list and the selection guard need each other (claim -> engine setters, engine -> claim
  // state), so the engine reaches the claim through a ref.
  const claimRef = useRef(null);
  const {
    adminPreview,
    eventName,
    imagePaths,
    selectedImages,
    setSelectedImages,
    timeLeft,
    formatTimeLeft,
    toggleImageSelection,
    syncError,
    isEmpty,
  } = useFaceGalleryEngine({
    maxSelectable: number,
    onCycle: () => claimRef.current && claimRef.current.refreshHeld(),
    isBlocked: (path) =>
      Boolean(claimRef.current && claimRef.current.isBlockedByOther(path)),
  });
  const claim = useFaceClaim({
    adminPreview,
    onLost: (reason) => {
      setSelectedImages([]);
      // Operator release: the guest starts over at Home (expiry keeps the old in-gallery notice).
      if (reason === "revoked") returnHomeAfterAdminRelease(navigate);
      else showAlert("err", MSG_LOST, 5000);
    },
    onRestore: (faces) => setSelectedImages(faces.slice(0, number)),
    onReleaseFailed: () => showAlert("err", MSG_RELEASE_UNCONFIRMED, 5000),
  });
  claimRef.current = claim;
  const heldNotice = useHeldSelectionGuard({ selectedImages, setSelectedImages, claim, adminPreview });

  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Next: the hold is acquired HERE (not on selection). Navigation happens only on editing/shared.
  const handleNext = async () => {
    if (submitting) return;
    setSubmitting(true);
    const result = await claim.acquireForSelection(selectedImages);
    if (mountedRef.current) setSubmitting(false);
    if (
      result.status === "stale" ||
      result.status === "busy" ||
      result.status === "flow_expired" ||
      !mountedRef.current
    )
      return;
    if (result.proceed) {
      goToBodyGallery();
    } else if (result.status === "revoked") {
      returnHomeAfterAdminRelease(navigate);
    } else {
      showAlert("err", messageForAcquire(result), 5000);
      if (result.status === "held") {
        setSelectedImages((current) =>
          current.filter((path) => !(result.heldFaces || []).includes(path))
        );
      }
      claim.refreshHeld(); // reconcile at once with the server's view
    }
  };

  // Navigate to the body gallery page and initiate background downloads
  const goToBodyGallery = () => {
    showLoading();
    // Background downloads outlive this page on purpose (the body gallery
    // reads their results) but only within THIS event visit: every shared
    // write below re-checks the captured scope, so a download that finishes
    // after the guest moved to another event (or a later visit of the same
    // one) writes nothing and redirects nowhere.
    const scope = getEventScope();
    const targetName = scope ? scope.name : eventName;
    try {
      sessionStorage.setItem("selectedFaces", JSON.stringify(selectedImages));
      sessionStorage.setItem("faceImageBlobUrls", JSON.stringify({}));
      sessionStorage.setItem(
        "faceImagesDownloadStatus",
        JSON.stringify({
          total: selectedImages.length,
          completed: 0,
          failed: 0,
          inProgress: true,
        })
      );
      const downloadPromises = selectedImages.map(async (path) => {
        try {
          const response = await fetch(`${serverUrl}/image${path}?size=full`);
          if (!response.ok) {
            const statusCode = response.status;
            console.error(`Failed to get full image: ${path}`);
            const error = new Error(`Failed to fetch ${path}`);
            error.statusCode = statusCode;
            throw error;
          }
          const imageBlob = await response.blob();
          if (!isCurrentScope(scope)) {
            return { success: false, path, stale: true };
          }
          const blobUrl = URL.createObjectURL(imageBlob);
          const currentUrls = JSON.parse(
            sessionStorage.getItem("faceImageBlobUrls") || "{}"
          );
          currentUrls[path] = blobUrl;
          sessionStorage.setItem(
            "faceImageBlobUrls",
            JSON.stringify(currentUrls)
          );
          const currentStatus = JSON.parse(
            sessionStorage.getItem("faceImagesDownloadStatus") || "{}"
          );
          currentStatus.completed += 1;
          sessionStorage.setItem(
            "faceImagesDownloadStatus",
            JSON.stringify(currentStatus)
          );
          return { success: true, path };
        } catch (error) {
          console.error(`Error downloading image: ${path}`, error);
          if (!isCurrentScope(scope)) {
            return { success: false, path, stale: true };
          }
          const currentStatus = JSON.parse(
            sessionStorage.getItem("faceImagesDownloadStatus") || "{}"
          );
          currentStatus.failed += 1;
          sessionStorage.setItem(
            "faceImagesDownloadStatus",
            JSON.stringify(currentStatus)
          );
          return {
            success: false,
            path,
            error: error.message,
            statusCode: error.statusCode || 0,
          };
        }
      });
      Promise.all(downloadPromises)
        .then((results) => {
          if (!isCurrentScope(scope)) return;
          const failures = results.filter((result) => !result.success);
          const finalStatus = JSON.parse(
            sessionStorage.getItem("faceImagesDownloadStatus") || "{}"
          );
          finalStatus.inProgress = false;
          sessionStorage.setItem(
            "faceImagesDownloadStatus",
            JSON.stringify(finalStatus)
          );
          if (failures.length > 0) {
            console.error(`${failures.length} downloads failed:`, failures);
            const notFoundError = failures.find((f) => f.statusCode === 404);
            const gifToShow = notFoundError ? gifFaceFailed : gifFailed;
            const gifDuration = notFoundError ? 17500 : 30000;
            localStorage.setItem(
              "pendingGif",
              JSON.stringify({
                gifUrl: gifToShow,
                navigateTo: `/${targetName}`,
                duration: gifDuration,
                timestamp: Date.now(),
              })
            );
          }
        })
        .catch((error) => {
          console.error("Error in background downloads:", error);
          if (!isCurrentScope(scope)) return;
          const finalStatus = JSON.parse(
            sessionStorage.getItem("faceImagesDownloadStatus") || "{}"
          );
          finalStatus.inProgress = false;
          finalStatus.error = error.message;
          sessionStorage.setItem(
            "faceImagesDownloadStatus",
            JSON.stringify(finalStatus)
          );
          localStorage.setItem(
            "pendingGif",
            JSON.stringify({
              gifUrl: gifFailed,
              navigateTo: `/${targetName}`,
              duration: 30000,
              timestamp: Date.now(),
            })
          );
        });
      navigate(`/${targetName}/body-gallery`);
    } catch (error) {
      console.error("Error in goToBodyGallery:", error);
      showGif(gifFailed, `/${targetName}`, 30000);
    } finally {
      hideLoading();
    }
  };

  // Navigate back to the home screen (explicit leave: the editing hold is released, best effort)
  const goBack = async () => {
    showLoading();
    claim.releaseNow();
    navigate(`/${eventName}`);
  };

  return (
    <div
      className={`swingie-facegallery-container${
        adminPreview ? " swingie-facegallery--preview" : ""
      }`}
    >
      {!adminPreview && (
      <div className="swingie-facegallery-header">
        <div className="swingie-facegallery-timer">
          <span className="swingie-facegallery-timer-text">
            הזמן שנותר לבחירה:
          </span>
          <span
            className={`swingie-facegallery-timer-clock ${
              timeLeft < 30 ? "swingie-facegallery-timer-clock-low" : ""
            }`}
          >
            {formatTimeLeft()}
          </span>
        </div>
        <h1 className="swingie-facegallery-title rtl">{number === 1 ? "בחרו את הראש שלכם" : "בחרו את הראשים שלכם"}</h1>
      </div>
      )}
      <HeldGalleryStatus claim={claim} notice={heldNotice} adminPreview={adminPreview} />
      <FaceGallerySyncChip show={syncError} />
      {isEmpty ? (
        <FaceGalleryEmpty />
      ) : (
      <div className="swingie-facegallery-grid">
        {imagePaths.map(({ path, image }, index) => {
          const isSelected = selectedImages.includes(path);
          // A head another guest holds ("תפוס") can never be selected (a selected one is dropped by the guard).
          const isHeld = claim.isBlockedByOther(path);
          const isSelectable =
            !isHeld && (selectedImages.length < number || isSelected);
          return (
            <div
              key={index}
              className={`swingie-facegallery-image-container ${
                isSelected ? "swingie-facegallery-image-selected" : ""
              } ${
                !isSelectable && !isHeld ? "swingie-facegallery-image-unselectable" : ""
              }`}
              aria-disabled={isHeld ? "true" : undefined}
              data-held={isHeld ? "true" : undefined}
              onClick={() => isSelectable && toggleImageSelection(path)}
            >
              <LazyLoadImage
                src={image}
                alt={`Face ${index}`}
                className="swingie-facegallery-image"
                effect="blur"
              />
              {isHeld && <HeldFaceOverlay state={claim.heldStateOf(path)} />}
              {isSelected && (
                <div className="swingie-facegallery-image-overlay">
                  <span className="text-white text-2xl">✔</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
      {!adminPreview && (
      <div className="swingie-facegallery-button-container">
        <button className="swingie-facegallery-button rtl" onClick={goBack}>
          חזרה
        </button>
        <button
          className={`swingie-facegallery-button rtl ${
            selectedImages.length === number
              ? ""
              : "swingie-facegallery-button-disabled"
          }`}
          onClick={handleNext}
          disabled={selectedImages.length !== number || submitting}
        >
          <span>הבא </span>
          <span style={{ fontSize: "18px", fontWeight: "normal" }}>
            ({selectedImages.length}/{number})
          </span>
        </button>
      </div>
      )}
    </div>
  );
};

export default SwingieFaceGalleryPage;
